import { refreshBookings } from "../../../utils/apis/refreshBookings";
import { BookingsData, createSingleBooking, createMultiBooking } from "../../../utils/apis/bookings_repository";
import { Text } from "../Text"
import { DatePicker } from "../../ui/inputs/DatePicker";
import { getDateTimeString, prettifyTime } from "../../../utils/helper";
import { useEffect, useState } from "react";
import { Button } from "../CustomButton";
import { TimePicker } from "../inputs/TimePicker";
import { DatePickerWithRange } from "../inputs/DateRangePicker";
import { SheetClose } from "../Sheets";
import { UserModel } from "@/utils/apis/user_repository";
import { useQuery } from "@tanstack/react-query";
import { getAllUsers } from "../../../utils/apis/user_repository";
import { Spinner } from "../Spinner";
import { getUserFromLocalStorage, getTimeFromDate } from "../../../utils/helper";
import { Autocomplete, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { showToast } from "../../../utils/toast";
import { getTenant } from "../../../utils/apis/tenants_repository";
import { bookingDays, createEvBooking, takesEvBookings } from "../../../utils/apis/ev_bookings";
import { canAskSpaces, spacesFor, Spaces, SpacesAsk } from "../../../utils/apis/availability";
import { moveWithArrows, Notice, ParkingCard, ParkingSpace, Segmented } from "./bookingForm";
import { notesFrom, spacesLine, useMonthSpaces } from "./bookingSpaces";

interface CreateBookingBodyProps {
    setDialogState: (state: boolean) => void;
}

const CreateBookingBody = ({ setDialogState }: CreateBookingBodyProps) => {

    const currentUser = getUserFromLocalStorage();

    const { data: tenantData } = useQuery({
        queryKey: ['tenantData'],
        queryFn: () => getTenant(currentUser.tenant),
    })

    // tenantData can be undefined
    // tenantData.fulldayDefaultTimes.startHour = 0800. we need to return only 8
    const tenantStartHour = tenantData?.fulldayDefaultTimes.startHour ? tenantData.fulldayDefaultTimes.startHour / 100 : 9;
    const tenantEndHour = tenantData?.fulldayDefaultTimes.endHour ? tenantData.fulldayDefaultTimes.endHour / 100 : 18;
    const prettyStartHour = prettifyTime(tenantData?.fulldayDefaultTimes.startHour ? tenantData?.fulldayDefaultTimes.startHour : 800);
    const prettyEndHour = prettifyTime(tenantData?.fulldayDefaultTimes.endHour ? tenantData?.fulldayDefaultTimes.endHour : 1800);
    ;
    const [selectedUser, setSelectedUser] = useState<UserModel>({ userId: currentUser.userId, name: currentUser.name, email: currentUser.email, role: currentUser.role, tenant: currentUser.tenant, bookingLimit: 0, futureBookingPeriod: 30, lastMinuteBookingPeriod: 1, cardholderId: "", gallagherId: "", vehicles: [], permanentParking: false });
    const [personChosen, setPersonChosen] = useState(false);
    const [bookingType, setBookingType] = useState("Single");
    const [parkingSpace, setParkingSpace] = useState<ParkingSpace>("Standard");
    const [bookingHours, setBookingHours] = useState<"FullDay" | "Custom">("Custom");
    const [startTime, updateStartTime] = useState<string>(
        // check if the current time is less than 9 am and set the time accordingly
        // DateTime.now().hour < 9 ? "09:00 AM" : getTimeFromDate(new Date())
        DateTime.now().hour < tenantStartHour ? prettyStartHour : getTimeFromDate(new Date())
    );
    const [endTime, updateEndTime] = useState<string>(
        // check if the start time is less than 9 am and set the time accordingly
        // DateTime.now().hour < 16 ? "06:00 PM" : getTimeFromDate(DateTime.now().plus({ hours: 3 }).toJSDate())
        DateTime.now().hour < tenantEndHour ? prettyEndHour : getTimeFromDate(DateTime.now().plus({ hours: 3 }).toJSDate())
    );
    const [startDate, setStartDate] = useState<Date>(
        DateTime.now().toJSDate()
    );
    const [endDate, setEndDate] = useState<Date>();
    const [shownMonth, setShownMonth] = useState<Date>(startDate);
    // A full day runs over the person's company's default hours (the admin's until someone is chosen). Today it
    // starts from now rather than in the past, as the app and the server keep a first day's given times.
    const { data: personTenant } = useQuery({
        queryKey: ['tenantData', selectedUser?.tenant],
        queryFn: () => getTenant(selectedUser!.tenant),
        enabled: personChosen && Boolean(selectedUser?.tenant),
    });
    const fullDayHours = (personTenant ?? tenantData)?.fulldayDefaultTimes;
    const fullDayStartHour = fullDayHours?.startHour || 800;
    const fullDayEnd = prettifyTime(fullDayHours?.endHour || 1800);
    const soon = DateTime.now().plus({ minutes: 5 - (DateTime.now().minute % 5) }).startOf("minute");
    const startsToday = DateTime.fromJSDate(startDate).hasSame(DateTime.now(), "day");
    const fullDayStart = startsToday && soon.hour * 100 + soon.minute > fullDayStartHour ? getTimeFromDate(soon.toJSDate()) : prettifyTime(fullDayStartHour);
    // The days the booking makes: one per day (skipping weekends where the company doesn't book them), each at
    // the chosen hours; a full day starting today starts from now on its first day only.
    const bookingIntervals = (includeWeekends: boolean) => {
        const first = bookingDays(startDate, startDate, bookingHours === "FullDay" ? fullDayStart : startTime, bookingHours === "FullDay" ? fullDayEnd : endTime, true)[0];
        if (bookingType === "Single") return [first];
        return bookingDays(startDate, endDate!, bookingHours === "FullDay" ? prettifyTime(fullDayStartHour) : startTime, bookingHours === "FullDay" ? fullDayEnd : endTime, includeWeekends)
            .map((day) => day.startTime.slice(0, 10) === first.startTime.slice(0, 10) ? { ...day, startTime: first.startTime } : day);
    };
    const effectiveStart = bookingHours === "FullDay" ? fullDayStart : startTime;
    const effectiveEnd = bookingHours === "FullDay" ? fullDayEnd : endTime;

    const [userId, setUserId] = useState<string>(currentUser.userId);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const queryClient = useQueryClient();


    const handleClickMulti = () => {
        setBookingType("Multi");
        setShownMonth(DateTime.now().toJSDate());
        setStartDate(DateTime.now().toJSDate());
        // No range until one is chosen: the first day clicked starts it and the second ends it.
        setEndDate(undefined);
    }

    const handleUserSelect = (userId: string) => {
        setUserId(userId);
    }

    const handleClickSingle = () => {
        setBookingType("Single");
        setShownMonth(DateTime.now().toJSDate());
        setStartDate(DateTime.now().toJSDate());
    }

    const handleStartTime = (time: string) => {
        updateStartTime(time);
    }

    const handleEndTime = (time: string): void => {
        updateEndTime(time);
    }

    const handleStartDate = (date: Date) => {

        // check if the date is not today, change the start time to 9 am
        if (date.getDate() !== DateTime.now().day) {
            updateStartTime(prettyStartHour);
            updateEndTime(prettyEndHour);
        } else {
            // check if the current time is less than 9 am and set the time accordingly
            updateStartTime(DateTime.now().hour < tenantStartHour ? prettyStartHour : getTimeFromDate(new Date()));
            updateEndTime(DateTime.now().hour < tenantEndHour - 2 ? prettyEndHour : getTimeFromDate(DateTime.now().plus({ hours: 3 }).toJSDate()));
        }

        setStartDate(date);
        setShownMonth(date);
    }

    const handleEndDate = (date: Date) => {
        setEndDate(date);
    }

    const createSingleBookingMutation = useMutation({
        mutationFn: createSingleBooking,
        onSuccess: async (value) => {
            if (value) {
                refreshBookings(queryClient);
                setDialogState(false);
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        },
    })

    // The parking type comes first, as in the booking app. A tenant admin's people share their company, so its
    // answer decides whether EV is offered; a super admin books across companies, so each person is checked.
    const superAdmin = currentUser.role === "SuperAdmin";
    const userData = getUserFromLocalStorage();

    const { data: allUsers, isLoading: dataLoading, isError } = useQuery({
        queryKey: ['users'],
        queryFn: () => getAllUsers(
            userData.role === "SuperAdmin" ? undefined : userData.tenant
        ),
    })
    // The admin's current record: the copy saved at sign-in can predate their company switching over.
    const me = allUsers?.find((user) => user.userId === currentUser.userId) ?? currentUser;
    const { data: ownCompanyEv = false, isLoading: checkingOwnCompany, isError: ownCompanyUnknown } = useQuery({
        queryKey: ['evBookings', currentUser.userId],
        queryFn: () => takesEvBookings(me),
        enabled: !superAdmin && Boolean(me?.capacityTenantId),
    });
    const evCardShown = superAdmin || ownCompanyEv;
    useEffect(() => {
        if (!evCardShown && parkingSpace === "EV") setParkingSpace("Standard");
    }, [evCardShown, parkingSpace]);

    // EV bays are only for a person whose company takes EV bookings (switched over, with EV turned on).
    const { data: personEv, isFetching: checkingPerson, isError: personUnknown } = useQuery({
        queryKey: ['evBookings', selectedUser?.userId],
        queryFn: () => takesEvBookings(selectedUser!),
        enabled: parkingSpace === "EV" && personChosen && Boolean(selectedUser?.capacityTenantId),
    });
    const bookingEv = parkingSpace === "EV";

    // Free spaces, as the booking app shows them: at the chosen person's company (a tenant admin's own until then).
    // A company that hasn't switched over is asked about by its record ID, and only answers its own people, so a
    // super admin booking for another company isn't shown its spaces.
    const spacesPerson: SpacesAsk | undefined = personChosen ?
        { ...selectedUser, companyId: !superAdmin || selectedUser.tenant === currentUser.tenant ? personTenant?.recordId : undefined } :
        superAdmin ? undefined : { ...me, companyId: tenantData?.recordId };
    const spacesKey = ['spaces', spacesPerson?.capacityTenantId, spacesPerson?.companyId, spacesPerson?.userId, effectiveStart, effectiveEnd];
    const hoursValid = bookingDays(startDate, startDate, effectiveStart, effectiveEnd, true).every((day) => day.endTime > day.startTime);
    // The calendar shows each day's spaces over the company's full day, whatever hours are chosen, so a day reads
    // as "spaces left that day"; the card and notices count at the chosen hours.
    const calendarStart = prettifyTime(fullDayStartHour);
    const calendarHoursValid = bookingDays(new Date(2030, 0, 7), new Date(2030, 0, 7), calendarStart, fullDayEnd, true)
        .every((day) => day.endTime > day.startTime);
    const shownMonths = bookingType === "Single" ? [shownMonth] : [shownMonth, DateTime.fromJSDate(shownMonth).plus({ months: 1 }).toJSDate()];
    const monthSpaces = [
        useMonthSpaces(spacesPerson, shownMonths[0], calendarStart, fullDayEnd, calendarHoursValid),
        useMonthSpaces(spacesPerson, shownMonths[1], calendarStart, fullDayEnd, calendarHoursValid),
    ];
    const dayNotes = notesFrom(monthSpaces, bookingEv ? "EV" : "Standard");
    // The person's company decides weekends once they're chosen; the check waits for it so it asks about the same days.
    const weekendCompany = personChosen ? personTenant : tenantData;
    const checkedDays = hoursValid && (bookingType === "Single" || endDate) && (!personChosen || personTenant) ?
        bookingIntervals(Boolean(weekendCompany?.isWeekend)) : [];
    const { data: bookingSpaces, isFetching: checkingSpaces, isError: spacesUnknown } = useQuery({
        queryKey: [...spacesKey, 'booking', JSON.stringify(checkedDays)],
        queryFn: async (): Promise<Spaces | null> => spacesFor(spacesPerson!, checkedDays),
        enabled: Boolean(canAskSpaces(spacesPerson) && checkedDays.length),
        staleTime: 60_000,
    });
    const spacesText = (space: ParkingSpace) => {
        if (!canAskSpaces(spacesPerson) || !hoursValid) return undefined;
        return spacesLine(bookingSpaces, space, checkingSpaces, spacesUnknown);
    };
    const chosenSpaces = bookingSpaces?.[bookingEv ? "ev" : "standard"];
    // EV needs a free bay; a Standard booking can still go ahead and the server decides, as in the app.
    const noEvSpace = bookingEv && chosenSpaces?.available === 0;
    const evRefusedForPerson = bookingEv && personChosen && !checkingPerson &&
        (!selectedUser?.capacityTenantId || personEv === false);
    const evCheckFailed = bookingEv && personChosen && !checkingPerson && personUnknown;
    // Why the service won't take an EV booking for them: their account or role, or else their company.
    const evRefusal = selectedUser?.role === "Disabled" ? `${selectedUser?.name}'s account is disabled, so they can't be booked an EV bay.` :
        selectedUser?.role === "SuperAdmin" && !superAdmin ? `${selectedUser?.name} is a super admin, so only a super admin can book for them.` :
        `${selectedUser?.name}'s company doesn't offer EV bays yet. Choose Standard, or someone else.`;
    const evCheckFailure = `We couldn't check EV parking for ${selectedUser?.name} just now. Please try again in a moment.`;

    const evBookingMutation = useMutation({
        mutationFn: async () => {
            const days = bookingIntervals(Boolean((personTenant ?? await getTenant(selectedUser.tenant))?.isWeekend));
            return createEvBooking(selectedUser, days, { timeType: bookingHours === "FullDay" ? "FullDay" : "Custom" });
        },
        onSuccess: (value) => {
            if (value) {
                refreshBookings(queryClient);
                setDialogState(false);
            }
            setIsLoading(false);
        },
        onError: () => {
            showToast(`We couldn't book an EV bay for ${selectedUser?.name || "this person"} just now. Please try again.`, { type: "error" });
            setIsLoading(false);
        },
    })

    const createMultiBookingMutation = useMutation({
        mutationFn: createMultiBooking,
        onSuccess: (value) => {
            if (value) {
                refreshBookings(queryClient);
                setDialogState(false);
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        },
    })

    const handleCreateBooking = async () => {
        // An EV booking can take a while to confirm; a second click would book again.
        if (isLoading) return;

        const now = DateTime.now();

        if ((!startDate || !endDate) && bookingType === "Multi") {
            showToast("One or more date fields are empty", {
                type: "warning"
            });
            return;
        }

        if (!startDate && bookingType === "Single") {
            showToast("Start date is empty", {
                type: "warning"
            });
            return;
        }

        if (!effectiveStart) {
            showToast("Start time is empty", {
                type: "warning"
            });
            return;
        }

        if (!effectiveEnd) {
            showToast("End time is empty", {
                type: "warning"
            });
            return;
        }

        if (!userId || !personChosen) {
            showToast("Choose who the booking is for.", {
                type: "warning"
            });
            return;
        }

        if (bookingEv && personEv !== true) {
            showToast(evCheckFailed ? evCheckFailure : evRefusal, {
                type: "warning"
            });
            return;
        }

        const updatedStartDate = getDateTimeString(startDate!.toISOString(), effectiveStart!);
        const updatedEndDate = getDateTimeString(bookingType === "Single" ? startDate!.toISOString() : endDate!.toISOString(), effectiveEnd!);


        if (DateTime.fromJSDate(new Date(updatedStartDate)) < now) {
            showToast("Start time cannot be in the past", {
                type: "warning"
            });
            return;
        }


        if (DateTime.fromJSDate(new Date(updatedStartDate)) > DateTime.fromJSDate(new Date(updatedEndDate))) {
            showToast("Start date cannot be after than the end date", {
                type: "warning"
            });
            return;
        }

        const boookingData: Partial<BookingsData> = {
            bookingType: bookingType,
            startTime: updatedStartDate,
            endTime: updatedEndDate,
            userId: userId!
        }
        setIsLoading(true);
        if (bookingEv) {
            evBookingMutation.mutate();
            return;
        }
        bookingType === "Single" ? createSingleBookingMutation.mutate(boookingData) : createMultiBookingMutation.mutate(boookingData);
    }

    if (isError) {
        return <div className="flex justify-center">Something went wrong</div>
    }

    const calendarCaption = canAskSpaces(spacesPerson) && calendarHoursValid ? `${parkingSpace} availability · ${calendarStart} – ${fullDayEnd} (full day) · Sydney time` : undefined;
    const plates = personChosen ? (selectedUser?.vehicles ?? []).filter((plate) => typeof plate === "string" && plate.trim()) : [];
    const dateText = bookingType === "Single" ? DateTime.fromJSDate(startDate).toFormat("d LLL yyyy") :
        `${DateTime.fromJSDate(startDate).toFormat("d LLL yyyy")} – ${endDate ? DateTime.fromJSDate(endDate).toFormat("d LLL yyyy") : "End date"}`;

    return (
        <>
            {dataLoading ? <div className="flex items-center flex-col justify-center">
                <Text as="span">Getting all User Details, Please wait</Text>
                <div className="mt-4">
                    <Spinner />
                </div>
            </div> : // The shared Sheet doesn't size its body, so this fills the panel (less its padding) and pins the footer.
                <div className="flex h-[calc(100dvh-3.5rem)] flex-col text-left">
                <div className="flex-1 overflow-y-auto pr-1 pb-6">
                    <div className="pt-10">
                        <Text as="h2">Create booking</Text>
                        <p className="mt-1 text-sm text-app-secondary">Choose the parking, then who it's for and when.</p>
                    </div>

                    {/* 1. Parking type */}
                    <section className="mt-6">
                        <h3 className="mb-3 text-lg font-bold text-app-text">Parking type</h3>
                        <div role="radiogroup" aria-label="Parking type" className="flex gap-3"
                            onKeyDown={(event) => evCardShown && moveWithArrows(event, ["Standard", "EV"] as ParkingSpace[], parkingSpace, setParkingSpace)}>
                            <ParkingCard space="Standard" selected={parkingSpace === "Standard"} onSelect={() => setParkingSpace("Standard")} detail={spacesText("Standard")} />
                            {evCardShown && <ParkingCard space="EV" selected={parkingSpace === "EV"} onSelect={() => setParkingSpace("EV")} detail={spacesText("EV")} />}
                        </div>
                        <p className="mt-2 text-xs text-app-secondary">
                            {!evCardShown ? (checkingOwnCompany ? "Checking EV parking for your company…" :
                                ownCompanyUnknown ? "We couldn't check EV parking just now, so bookings are Standard for the moment." :
                                "EV bays aren't open for your company yet, so bookings are Standard.") :
                                bookingSpaces ? (bookingType === "Single" ? "For the selected date and hours." : "Fewest spaces across the selected dates and hours.") :
                                superAdmin && !personChosen ? "Choose who it's for to see free spaces." : "Choose the parking type they need."}
                        </p>
                    </section>

                    {/* 2. Who it's for */}
                    <section className="mt-6">
                        <h3 className="mb-3 text-lg font-bold text-app-text">Who's it for?</h3>
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            options={allUsers!.map((user) => user.name)}
                            // disable options for vip users
                            getOptionDisabled={(option) => {
                                return allUsers!.find((user) => user.name === option)?.permanentParking!;
                            }}
                            value={personChosen ? selectedUser?.name : null}
                            onChange={(event, value) => {
                                const person = allUsers!.find((user) => user.name === value);
                                setSelectedUser(person!);
                                setPersonChosen(Boolean(person));
                                handleUserSelect(person?.userId!);
                            }}
                            fullWidth
                            renderInput={(params) => <TextField {...params} label="Select Users" />}
                        />
                        <p className="mt-2 text-xs text-app-secondary">People with permanent parking don't need a booking, so they can't be chosen.</p>
                        {bookingEv && personChosen && checkingPerson && <p className="mt-3 text-xs text-app-secondary">Checking EV parking for {selectedUser?.name}…</p>}
                        {(evRefusedForPerson || evCheckFailed) && (
                            <div className="mt-3">
                                <Notice>{evCheckFailed ? evCheckFailure : evRefusal}</Notice>
                            </div>
                        )}
                    </section>

                    {/* 3. When */}
                    <section className="mt-6 space-y-4 rounded-[20px] border border-app-border bg-white p-5">
                        <h3 className="text-lg font-bold text-app-text">When are you parking?</h3>
                        <Segmented label="Booking length" value={bookingType}
                            options={[{ value: "Single", label: "Single day" }, { value: "Multi", label: "Multiple days" }]}
                            onChange={(value) => value === "Single" ? handleClickSingle() : handleClickMulti()} />
                        <div>
                            <p className="mb-2 text-sm font-semibold text-app-text">{bookingType === "Single" ? "Date" : "Dates"}</p>
                            {bookingType === "Single" ?
                                <DatePicker setNewDate={(value) => handleStartDate(value)} initialDate={startDate} dayNotes={dayNotes} caption={calendarCaption} onMonthChange={setShownMonth} month={shownMonth} /> :
                                <DatePickerWithRange startEmpty setStartDate={(val) => handleStartDate(val)} setEndDate={(val) => handleEndDate(val)} startDate={startDate!} endDate={endDate!}
                                    dayNotes={dayNotes} caption={calendarCaption} onMonthChange={setShownMonth} month={shownMonth} />}
                            <p className="mt-2 text-xs text-app-secondary">See {parkingSpace} availability in the calendar.</p>
                        </div>
                        <Segmented label="Booking hours" value={bookingHours}
                            options={[{ value: "FullDay", label: "Full day" }, { value: "Custom", label: "Custom hours" }]}
                            onChange={setBookingHours} />
                        <div className="flex gap-6">
                            <div>
                                <p className="mb-2 text-sm font-semibold text-app-text">Start time</p>
                                {bookingHours === "FullDay" ? <p className="text-sm text-app-secondary">{fullDayStart}</p> :
                                    <TimePicker initialTime={startTime} onChange={handleStartTime} />}
                            </div>
                            <div>
                                <p className="mb-2 text-sm font-semibold text-app-text">End time</p>
                                {bookingHours === "FullDay" ? <p className="text-sm text-app-secondary">{fullDayEnd}</p> :
                                    <TimePicker initialTime={endTime} onChange={handleEndTime} />}
                            </div>
                        </div>
                        <p className="text-xs text-app-secondary">All times are Sydney time.</p>
                        {canAskSpaces(spacesPerson) && !hoursValid && <Notice>Choose valid dates and hours to check availability.</Notice>}
                        {chosenSpaces && (chosenSpaces.available > 0 ?
                            <Notice tone="success">{parkingSpace} parking is available.</Notice> :
                            <Notice>No {parkingSpace} spaces for these dates and hours. Choose another date or type.</Notice>)}
                        {spacesUnknown && <Notice>{bookingEv ? "Availability couldn't be checked. Please try again in a moment." :
                            "Availability couldn't be checked. You can still book Standard parking."}</Notice>}
                    </section>

                    {/* 4. Their vehicles */}
                    {personChosen && (
                        <section className="mt-6">
                            <h3 className="mb-3 text-lg font-bold text-app-text">Registered vehicles</h3>
                            {plates.length ? (
                                <div className="flex flex-wrap gap-2">
                                    {plates.map((plate) => <span key={plate} className="rounded-lg bg-app-muted px-3 py-2 text-sm font-semibold text-app-text">{plate}</span>)}
                                </div>
                            ) : (
                                <Notice>{selectedUser?.gallagherId?.trim() ? `${selectedUser?.name} has no registered vehicle; their access card is used instead.` :
                                    `${selectedUser?.name} has no registered vehicle or access card. Add one to their profile before booking.`}</Notice>
                            )}
                            {plates.length > 0 && <p className="mt-2 text-xs text-app-secondary">Vehicles linked to {selectedUser?.name}'s profile.</p>}
                        </section>
                    )}
                </div>

                {/* Summary and actions, as at the foot of the booking app's form */}
                <div className="border-t border-app-border pt-4 pb-6">
                    <p className="text-sm text-app-text">{parkingSpace} · {dateText}{personChosen ? ` · ${selectedUser?.name}` : ""}</p>
                    <p className="text-sm text-app-secondary">{effectiveStart} – {effectiveEnd}</p>
                    <div className="mt-4 flex gap-3">
                        <SheetClose asChild>
                            <Button variant={"secondary"} className="flex-1 border border-primary text-primaryText">Cancel</Button>
                        </SheetClose>
                        <Button variant={"default"} onClick={handleCreateBooking} isLoading={isLoading} disabled={evRefusedForPerson || evCheckFailed || (bookingEv && personChosen && checkingPerson) || noEvSpace}
                            className="flex-[2] text-white disabled:opacity-50">Create booking</Button>
                    </div>
                </div>
            </div>}
        </>
    );
}

export default CreateBookingBody;
